import { useRef, useState, useEffect, useMemo } from 'react';
import {
  Box,
  Center,
  Field,
  Flex,
  HStack,
  SimpleGrid,
  Spinner,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  DataTable,
  StatusBadge,
  Button as ButtonComponent,
  SelectInput,
  InputField,
  Textarea,
  type Column,
  type StatusType,
} from '@themes/aegix';
import { DrawerComponent } from '../ui/drawerComponent';
import { useForm, Controller } from 'react-hook-form';
import type {
  RosterProvider,
  RosterProviderWrite,
  ProviderType,
  SyncMode,
  SyncHistoryEntry,
  CsvUploadResult,
} from '../../types/RosterTypes';
import {
  PROVIDER_TYPE_LABELS,
  AUTH_FIELDS_BY_TYPE,
} from '../../types/RosterTypes';
import { useUploadCsv } from '../../hooks/queries/useRoster';
import { toaster } from '../ui/toaster';
import { useTranslation } from 'react-i18next';
import { useRosterCrud, type ProviderRow } from './hooks/useRosterCrud';

// =============================================================================
// Constants
// =============================================================================

const PROVIDER_TYPE_OPTIONS = Object.entries(PROVIDER_TYPE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

// =============================================================================
// Main Component
// =============================================================================

export function RosterContent() {
  const { t } = useTranslation();

  const {
    providers,
    isLoading,
    error,
    rows,
    columns,
    // Main drawer
    drawerOpen,
    editItem,
    testResult,
    testingId,
    handleOpenCreate,
    handleOpenEdit,
    handleClose,
    handleSubmit,
    handleDelete,
    handleTestConnection,
    initialValues,
    // Sync drawer
    syncDrawerOpen,
    syncProvider,
    syncMutation,
    handleSync,
    handleOpenSync,
    handleCloseSync,
    // History drawer
    historyProvider,
    syncStatus,
    handleCloseHistory,
    handleOpenHistory,
    // CSV drawer
    csvDrawerOpen,
    csvProvider,
    uploadMutation,
    handleOpenCsv,
    handleCloseCsv,
    // Rendering
    STATUS_MAP,
  } = useRosterCrud();

  // RHF setup for provider form
  const methods = useForm<RosterProviderWrite>({
    defaultValues: initialValues,
  });

  const {
    control,
    handleSubmit: rhfHandleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  // enableReinitialize equivalent. No react-hooks/exhaustive-deps disable here:
  // sms-react's eslint config doesn't load the react-hooks plugin, so the
  // directive itself errors ("Definition for rule not found").
  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const formValues = watch();
  const authFields =
    AUTH_FIELDS_BY_TYPE[formValues.provider_type as ProviderType] ?? [];

  const onSubmit = (values: RosterProviderWrite) => {
    handleSubmit(values, methods.setError);
  };

  // Add render function to status column
  const renderColumns = columns.map((col) => {
    if (col.key === 'status') {
      return {
        ...col,
        render: (row: ProviderRow) => {
          const info = STATUS_MAP[row.status] ?? {
            label: row.status,
            status: 'inactive' as StatusType,
          };
          return <StatusBadge status={info.status} label={info.label} />;
        },
      };
    }
    return col;
  });

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <Box px={5} py={5}>
      <VStack align="stretch" gap={5}>
        {/* Header */}
        <Flex justify="flex-end" align="center">
          <ButtonComponent visual="primary" onClick={handleOpenCreate} data-e2e="add-provider-button">
            Add Provider
          </ButtonComponent>
        </Flex>

        {/* Provider list */}
        {isLoading ? (
          <Center py={8}>
            <Spinner size="md" color="button.primary" />
          </Center>
        ) : error ? (
          <Text color="text.danger" fontSize="sm">
            Failed to load providers
          </Text>
        ) : rows.length === 0 ? (
          <Center py={8}>
            <Text color="text.secondary" fontSize="sm">
              No roster providers configured
            </Text>
          </Center>
        ) : (
          <>
            <DataTable
              columns={renderColumns}
              data={rows}
              keyExtractor={(row) => row.id}
              onRowClick={(row) => {
                const item = providers?.find((p) => String(p.id) === row.id);
                if (item) handleOpenEdit(item);
              }}
            />

            {/* Action buttons per provider */}
            <VStack align="stretch" gap={3}>
              {(providers ?? []).map((p) => (
                <Box
                  key={p.id}
                  bg="surface.searchInput"
                  borderRadius="lg"
                  p={3}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    wrap="wrap"
                    gap={2}
                  >
                    <Text fontSize="sm" fontWeight="500" color="text.primary">
                      {p.display_name}
                    </Text>
                    <HStack gap={2} wrap="wrap">
                      <ButtonComponent
                        visual="secondary"
                        size="sm"
                        onClick={() => handleTestConnection(p)}
                        disabled={testingId === p.id}
                        data-e2e="test-connection-button">
                        {testingId === p.id
                          ? t('roster.testing', 'Testing…')
                          : t('roster.testConnection', 'Test Connection')}
                      </ButtonComponent>
                      <ButtonComponent
                        visual="secondary"
                        size="sm"
                        onClick={() => handleOpenSync(p)}
                        data-e2e="sync-now-button">
                        {t('roster.syncNow', 'Sync Now')}
                      </ButtonComponent>
                      <ButtonComponent
                        visual="secondary"
                        size="sm"
                        onClick={() => handleOpenHistory(p)}
                        data-e2e="history-button">
                        {t('roster.history', 'History')}
                      </ButtonComponent>
                      <ButtonComponent
                        visual="secondary"
                        size="sm"
                        onClick={() => handleOpenCsv(p)}
                        data-e2e="csv-import-button">
                        {t('roster.csvImport', 'CSV Import')}
                      </ButtonComponent>
                    </HStack>
                  </Flex>

                  {/* Inline test result */}
                  {testResult &&
                    testResult.success !== undefined &&
                    editItem === null &&
                    testingId === null && (
                      <Box mt={2} p={2} bg="surface.primary" borderRadius="md">
                        <Text
                          fontSize="xs"
                          color={testResult.success ? 'green.500' : 'red.500'}
                          fontWeight="500"
                        >
                          {testResult.message}
                        </Text>
                        {testResult.endpoints_tested &&
                          Object.keys(testResult.endpoints_tested).length >
                          0 && (
                            <VStack align="stretch" gap={1} mt={1}>
                              {Object.entries(testResult.endpoints_tested).map(
                                ([endpoint, ok]) => (
                                  <Text
                                    key={endpoint}
                                    fontSize="xs"
                                    color={ok ? 'green.500' : 'red.500'}
                                  >
                                    {ok ? '\u2713' : '\u2717'} {endpoint}
                                  </Text>
                                ),
                              )}
                            </VStack>
                          )}
                      </Box>
                    )}

                  {/* Last error */}
                  {p.last_error && (
                    <Box mt={2}>
                      <Text fontSize="xs" color="text.danger">
                        Last error: {p.last_error}
                      </Text>
                    </Box>
                  )}
                </Box>
              ))}
            </VStack>
          </>
        )}
      </VStack>

      {/* ================================================================= */}
      {/* Add / Edit Provider Drawer                                        */}
      {/* ================================================================= */}
      <DrawerComponent
        isOpen={drawerOpen}
        onClose={handleClose}
        title={editItem ? 'Edit Provider' : 'Add Provider'}
        size="md"
        footer={
          <SimpleGrid columns={editItem ? 3 : 2} gap={4} width="100%">
            {editItem && (
              <ButtonComponent
                visual="alert"
                type="button"
                onClick={handleDelete}
                data-e2e="delete-button">
                Delete
              </ButtonComponent>
            )}
            <ButtonComponent
              visual="secondary"
              type="button"
              onClick={handleClose}
              data-e2e="cancel-button">
              Cancel
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              type="submit"
              form="provider-form"
              data-e2e="save-button">
              Save
            </ButtonComponent>
          </SimpleGrid>
        }
      >
        <form id="provider-form" onSubmit={rhfHandleSubmit(onSubmit)}>
          <VStack align="stretch" gap={4}>
            {/* Provider Type */}
            <Field.Root invalid={!!errors.provider_type}>
              <Field.Label htmlFor="roster-provider-type">{t('roster.providerType', 'Provider Type')}</Field.Label>
              {editItem ? (
                <InputField
                  id="roster-provider-type"
                  value={
                    PROVIDER_TYPE_LABELS[
                    formValues.provider_type as ProviderType
                    ] ?? formValues.provider_type
                  }
                  readOnly
                  bg="surface.searchInput"
                />
              ) : (
                <Controller
                  name="provider_type"
                  control={control}
                  render={({ field }) => (
                    <SelectInput
                      value={field.value}
                      option={PROVIDER_TYPE_OPTIONS}
                      onChange={(val: string) => field.onChange(val)}
                      searchable={false}
                      e2eTitle="roster-provider-type-select"
                    />
                  )}
                />
              )}
              {errors.provider_type && (
                <Field.ErrorText data-e2e="roster-provider-type-error">
                  {errors.provider_type.message ? t(errors.provider_type.message) : null}
                </Field.ErrorText>
              )}
            </Field.Root>

            {/* Display Name */}
            <Field.Root invalid={!!errors.display_name}>
              <Field.Label htmlFor="roster-display-name">{t('roster.displayName', 'Display Name')}</Field.Label>
              <InputField
                id="roster-display-name"
                name="display_name"
                value={formValues.display_name}
                onChange={(e) => setValue('display_name', e.target.value)}
              />
              {errors.display_name && (
                <Field.ErrorText data-e2e="roster-display-name-error">{errors.display_name.message ? t(errors.display_name.message) : null}</Field.ErrorText>
              )}
            </Field.Root>

            {/* Base URL */}
            <Field.Root invalid={!!errors.base_url}>
              <Field.Label htmlFor="roster-base-url">{t('roster.baseUrl', 'Base URL')}</Field.Label>
              <InputField
                id="roster-base-url"
                name="base_url"
                value={formValues.base_url}
                onChange={(e) => setValue('base_url', e.target.value)}
                placeholder="https://sis.example.com/ims/oneroster/v1p2"
              />
              {errors.base_url && (
                <Field.ErrorText data-e2e="roster-base-url-error">{errors.base_url.message ? t(errors.base_url.message) : null}</Field.ErrorText>
              )}
            </Field.Root>

            {/* Auth Config (dynamic) */}
            {authFields.length > 0 && (
              <Box>
                <Text
                  fontSize="sm"
                  fontWeight="600"
                  color="text.primary"
                  mb={2}
                >
                  Authentication
                </Text>
                {editItem && (
                  <Text fontSize="xs" color="text.secondary" mb={2}>
                    Leave blank to keep existing credentials.
                  </Text>
                )}
                <VStack align="stretch" gap={3}>
                  {authFields.map((field) => (
                    <Field.Root key={field.key}>
                      <Field.Label htmlFor={`roster-auth-${field.key}`}>
                        {field.label}
                      </Field.Label>
                      {field.type === 'textarea' ? (
                        <Textarea
                          id={`roster-auth-${field.key}`}
                          value={formValues.auth_config?.[field.key] ?? ''}
                          onChange={(e) =>
                            setValue(`auth_config.${field.key}`, e.target.value)
                          }
                          rows={3}
                          placeholder={
                            editItem?.auth_config_summary?.[`has_${field.key}`]
                              ? '(configured \u2014 leave blank to keep)'
                              : ''
                          }
                          data-e2e="auth-config-textarea" />
                      ) : (
                        <InputField
                          id={`roster-auth-${field.key}`}
                          type={field.type}
                          value={formValues.auth_config?.[field.key] ?? ''}
                          onChange={(e) =>
                            setValue(`auth_config.${field.key}`, e.target.value)
                          }
                          placeholder={
                            editItem?.auth_config_summary?.[`has_${field.key}`]
                              ? '(configured \u2014 leave blank to keep)'
                              : ''
                          }
                        />
                      )}
                    </Field.Root>
                  ))}
                </VStack>
              </Box>
            )}

            {/* Sync Config */}
            <Box>
              <Text fontSize="sm" fontWeight="600" color="text.primary" mb={2}>
                Sync Configuration
              </Text>
              <SimpleGrid columns={2} gap={4}>
                <Field.Root>
                  <Field.Label htmlFor="roster-delta-sync">Delta Sync (minutes)</Field.Label>
                  <InputField
                    id="roster-delta-sync"
                    type="number"
                    value={formValues.sync_config?.delta_sync_minutes ?? ''}
                    onChange={(e) =>
                      setValue(
                        'sync_config.delta_sync_minutes',
                        e.target.value ? Number(e.target.value) : undefined,
                      )
                    }
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label htmlFor="roster-full-sync">Full Sync (hours)</Field.Label>
                  <InputField
                    id="roster-full-sync"
                    type="number"
                    value={formValues.sync_config?.full_sync_hours ?? ''}
                    onChange={(e) =>
                      setValue(
                        'sync_config.full_sync_hours',
                        e.target.value ? Number(e.target.value) : undefined,
                      )
                    }
                  />
                </Field.Root>
              </SimpleGrid>
            </Box>

            {/* Status Toggle */}
            <Field.Root>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Switch.Root
                    checked={field.value === 'active'}
                    onCheckedChange={(d) =>
                      field.onChange(d.checked ? 'active' : 'disabled')
                    }
                  >
                    <Switch.HiddenInput data-e2e="roster-active-switch" />
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                    <Switch.Label>Active</Switch.Label>
                  </Switch.Root>
                )}
              />
            </Field.Root>
          </VStack>
        </form>
      </DrawerComponent>

      {/* ================================================================= */}
      {/* Sync Now Drawer                                                   */}
      {/* ================================================================= */}
      <SyncDrawer
        isOpen={syncDrawerOpen}
        provider={syncProvider}
        onClose={handleCloseSync}
        onSync={handleSync}
        isPending={syncMutation.isPending}
      />

      {/* ================================================================= */}
      {/* Sync History Drawer                                               */}
      {/* ================================================================= */}
      <SyncHistoryDrawer
        isOpen={historyProvider !== null}
        provider={historyProvider}
        entries={syncStatus?.recent_syncs ?? []}
        onClose={handleCloseHistory}
      />

      {/* ================================================================= */}
      {/* CSV Upload Drawer                                                 */}
      {/* ================================================================= */}
      <CsvUploadDrawer
        isOpen={csvDrawerOpen}
        provider={csvProvider}
        onClose={handleCloseCsv}
        uploadMutation={uploadMutation}
      />
    </Box>
  );
}

// =============================================================================
// Sync Drawer
// =============================================================================

function SyncDrawer({
  isOpen,
  provider,
  onClose,
  onSync,
  isPending,
}: {
  isOpen: boolean;
  provider: RosterProvider | null;
  onClose: () => void;
  onSync: (mode: SyncMode) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<SyncMode>('full');

  const SYNC_MODE_OPTIONS = useMemo(
    () => [
      { value: 'full', label: t('roster.fullSync', 'Full Sync') },
      { value: 'delta', label: t('roster.deltaSync', 'Delta Sync') },
    ],
    [t],
  );

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={`Sync \u2014 ${provider?.display_name ?? ''}`}
      size="sm"
      footer={
        <SimpleGrid columns={2} gap={4} width="100%">
          <ButtonComponent visual="secondary" onClick={onClose} data-e2e="cancel-button">
            Cancel
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            onClick={() => onSync(mode)}
            disabled={isPending}
            data-e2e="start-sync-button">
            {isPending ? 'Dispatching\u2026' : 'Start Sync'}
          </ButtonComponent>
        </SimpleGrid>
      }
    >
      <VStack align="stretch" gap={4}>
        <Field.Root>
          <Field.Label htmlFor="roster-sync-mode">Sync Mode</Field.Label>
          <SelectInput
            id="roster-sync-mode"
            value={mode}
            option={SYNC_MODE_OPTIONS}
            onChange={(val: string) => setMode(val as SyncMode)}
            searchable={false}
            e2eTitle="roster-sync-mode-select"
          />
        </Field.Root>
      </VStack>
    </DrawerComponent>
  );
}

// =============================================================================
// Sync History Drawer
// =============================================================================

interface HistoryRow {
  id: string;
  sync_type: string;
  status: string;
  started_at: string;
  completed_at: string;
  counts: string;
  errors: string;
}

function SyncHistoryDrawer({
  isOpen,
  provider,
  entries,
  onClose,
}: {
  isOpen: boolean;
  provider: RosterProvider | null;
  entries: SyncHistoryEntry[];
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const historyCols: Column<HistoryRow>[] = useMemo(() => [
    { key: 'sync_type', label: t('common.type', 'Type'), width: '80px' },
    {
      key: 'status',
      label: t('common.status', 'Status'),
      width: '100px',
      render: (row) => {
        const s =
          row.status === 'completed'
            ? ('active' as StatusType)
            : row.status === 'failed'
              ? ('error' as StatusType)
              : ('warning' as StatusType);
        return <StatusBadge status={s} label={row.status} />;
      },
    },
    { key: 'started_at', label: t('roster.started', 'Started'), width: '160px' },
    { key: 'completed_at', label: t('roster.completed', 'Completed'), width: '160px' },
    { key: 'counts', label: t('roster.counts', 'Counts'), width: '260px' },
    { key: 'errors', label: t('roster.errorsWarnings', 'Errors/Warnings'), width: '160px' },
  ], [t]);

  const historyRows: HistoryRow[] = entries.map((e) => ({
    id: String(e.id),
    sync_type: e.sync_type,
    status: e.status,
    started_at: new Date(e.started_at).toLocaleString(),
    completed_at: e.completed_at
      ? new Date(e.completed_at).toLocaleString()
      : '\u2014',
    counts: [
      e.counts.schools_synced != null && `${e.counts.schools_synced} schools`,
      e.counts.persons_created != null && `${e.counts.persons_created} created`,
      e.counts.persons_updated != null && `${e.counts.persons_updated} updated`,
      e.counts.persons_deactivated != null &&
      `${e.counts.persons_deactivated} deactivated`,
      e.counts.enrollments_synced != null &&
      `${e.counts.enrollments_synced} enrollments`,
    ]
      .filter(Boolean)
      .join(', '),
    errors:
      e.error_count || e.warning_count
        ? `${e.error_count} errors, ${e.warning_count} warnings`
        : '\u2014',
  }));

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={`Sync History \u2014 ${provider?.display_name ?? ''}`}
      size="xl"
      footer={
        <ButtonComponent visual="secondary" onClick={onClose} data-e2e="close-button">
          Close
        </ButtonComponent>
      }
    >
      {historyRows.length === 0 ? (
        <Center py={8}>
          <Text color="text.secondary" fontSize="sm">
            No sync history available
          </Text>
        </Center>
      ) : (
        <DataTable
          columns={historyCols}
          data={historyRows}
          keyExtractor={(row) => row.id}
        />
      )}
    </DrawerComponent>
  );
}

// =============================================================================
// CSV Upload Drawer
// =============================================================================

function CsvUploadDrawer({
  isOpen,
  provider,
  onClose,
  uploadMutation,
}: {
  isOpen: boolean;
  provider: RosterProvider | null;
  onClose: () => void;
  uploadMutation: ReturnType<typeof useUploadCsv>;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<CsvUploadResult | null>(
    null,
  );

  const handleUpload = () => {
    if (!selectedFile || !provider) return;
    uploadMutation.mutate(
      { file: selectedFile, providerConfigId: provider.id },
      {
        onSuccess: (result) => {
          setUploadResult(result);
          toaster.create({
            title: 'Upload complete',
            description: `${result.persons_created} created, ${result.persons_updated} updated`,
            type: 'success',
          });
        },
        onError: (err) => {
          toaster.create({
            title: 'Upload failed',
            description: err.message,
            type: 'error',
          });
        },
      },
    );
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadResult(null);
    onClose();
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={handleClose}
      title={`CSV Import \u2014 ${provider?.display_name ?? ''}`}
      size="md"
      footer={
        <SimpleGrid columns={2} gap={4} width="100%">
          <ButtonComponent visual="secondary" onClick={handleClose} data-e2e="close-button">
            Close
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            onClick={handleUpload}
            disabled={!selectedFile || uploadMutation.isPending}
            data-e2e="upload-button">
            {uploadMutation.isPending ? 'Uploading\u2026' : 'Upload'}
          </ButtonComponent>
        </SimpleGrid>
      }
    >
      <VStack align="stretch" gap={4}>
        <Text fontSize="sm" color="text.secondary">
          Upload a .zip file containing OneRoster CSV files, or a single .csv
          file.
        </Text>

        <Box
          border="2px dashed"
          borderColor="border.secondary"
          borderRadius="lg"
          p={6}
          textAlign="center"
          cursor="pointer"
          onClick={() => fileInputRef.current?.click()}
          _hover={{ borderColor: 'button.primary' }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip,.csv"
            aria-label="Roster ZIP or CSV file"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              setSelectedFile(file);
              setUploadResult(null);
            }}
          />
          <Text fontSize="sm" color="text.primary">
            {selectedFile ? selectedFile.name : 'Click to select a file'}
          </Text>
          {selectedFile && (
            <Text fontSize="xs" color="text.secondary" mt={1}>
              {(selectedFile.size / 1024).toFixed(1)} KB
            </Text>
          )}
        </Box>

        {/* Upload result summary */}
        {uploadResult && (
          <Box bg="surface.searchInput" borderRadius="lg" p={4}>
            <Text fontSize="sm" fontWeight="600" color="text.primary" mb={2}>
              Import Results
            </Text>
            <VStack align="stretch" gap={1}>
              <Text fontSize="xs" color="text.primary">
                Persons created: {uploadResult.persons_created}
              </Text>
              <Text fontSize="xs" color="text.primary">
                Persons updated: {uploadResult.persons_updated}
              </Text>
              <Text fontSize="xs" color="text.primary">
                Persons deactivated: {uploadResult.persons_deactivated}
              </Text>
              <Text fontSize="xs" color="text.primary">
                Schools synced: {uploadResult.schools_synced}
              </Text>
              <Text fontSize="xs" color="text.primary">
                Enrollments synced: {uploadResult.enrollments_synced}
              </Text>
            </VStack>

            {uploadResult.errors.length > 0 && (
              <Box mt={3}>
                <Text fontSize="xs" fontWeight="600" color="text.danger" mb={1}>
                  Errors ({uploadResult.errors.length})
                </Text>
                {uploadResult.errors.map((err, i) => (
                  <Text key={i} fontSize="xs" color="text.danger">
                    {err}
                  </Text>
                ))}
              </Box>
            )}

            {uploadResult.warnings.length > 0 && (
              <Box mt={3}>
                <Text fontSize="xs" fontWeight="600" color="orange.500" mb={1}>
                  Warnings ({uploadResult.warnings.length})
                </Text>
                {uploadResult.warnings.map((w, i) => (
                  <Text key={i} fontSize="xs" color="orange.500">
                    {w}
                  </Text>
                ))}
              </Box>
            )}
          </Box>
        )}
      </VStack>
    </DrawerComponent>
  );
}
